import { glue } from 'femo';
import { fieldStateKeysRecord } from '../Field/constants';
import { formStateKeysRecord } from '../Form/constants';

import type {
  FieldInstance,
  FieldState, FNode,
  FormInstance,
  FormState,
} from '../interface';
import nodeHelper from './nodeHelper';

class InstanceHelper {
  createInstance = <V>(
    initState: Partial<FieldState<V> | FormState<V>>,
    reducer?: <D>(s: typeof initState, data: D) => typeof initState,
    formInstance?: FormInstance<V>,
    validParent?: FNode,
  ): FormInstance<V> | FieldInstance<V> => {
    // value 不在，initialValue 存在，则使用 initialValue 替换 value
    // 这里替换成 value 不会造成受控，因为传入的属性中没有 value
    // 如果 node 自身都没有 value 和 initialValue，则使用表单的 value 和 initialValue
    if (
      !(fieldStateKeysRecord.value in initState)
    ) {
      if (fieldStateKeysRecord.initialValue in initState) {
        initState.value = initState.initialValue;
      } else {
        // 当前节点非匿名才去取表单中的值
        // eslint-disable-next-line no-lonely-if
        if (!nodeHelper.isAnonymous(initState?.name)) {
          const propPath = nodeHelper.getNodePathUnderTheContext(validParent, formInstance?.node);
          propPath.push(initState?.name);

          // 先走表单 value 赋值
          if (formStateKeysRecord.value in (formInstance?.state || {})) {
            initState.value = nodeHelper.getValueByPath(formInstance?.state?.value, propPath);
          } else if (formStateKeysRecord.initialValue in (formInstance?.state || {})) {
            initState.value = nodeHelper.getValueByPath(formInstance?.state?.initialValue, propPath);
          }
        }
      }
    }
    const model = reducer
      ? glue((state, data) => {
        return reducer(state, data);
      }, initState)
      : glue(initState);

    model.config({
      updatePolicy: 'merge',
    });
    return {
      model,
      validate: () => Promise.resolve(model()?.value),
    };
  };

  merge = <O extends Record<string, any>, S extends Record<string, any>>(target: O, source: S): O => {
    Object.keys(source || {}).reduce((pre, key) => {
      const value = source?.[key];
      if (Object.is(value, undefined) || Object.is(value, null)) {
        return pre;
      }
      pre[key] = value;
      return pre;
    }, {} as Record<string, any>);

    return {
      ...target,
      ...source,
    };
  }

  filterState = <P extends Record<string, any>>(props: P, keys: (string | number)[]) => {
    return keys.reduce((pre, cur) => {
      // 在props中传入了的才会控制state
      if (`${cur}` in props) {
        pre[cur] = props[cur];
      }
      return pre;
    }, {} as Record<string, any>);
  }
}

export default new InstanceHelper();
